import { getSolutionValueDecimal } from "./getSolutionValueDecimal.ts";
import {
  CostScore,
  SolutionMap,
  SolutionOptionKey,
} from "@labs/shared/types/Solution.ts";
import {
  NullablePlanComponent,
  PlanComponentCategory,
} from "@labs/shared/types/AnalyzedPlan.ts";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";
import { combineFuelImpacts } from "./combineFuelImpacts.ts";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.ts";

export const getOtherComponent = (
  solutionMap: SolutionMap,
  baselineCarbonWithGrowthAndCleanerGrid: EmissionsBySource,
  efficiencyComponent: NullablePlanComponent,
  stationaryElectrificationComponent: NullablePlanComponent,
  mobileElectrificationComponent: NullablePlanComponent,
  buyCleanComponent: NullablePlanComponent,
) => {
  const refrigerantLeakReduction = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.RefrigerantLeaksRefrigerants,
  );
  const lowGwpRefrigerants = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowGwpRefrigerantsRefrigerants,
  );
  const refrigerantReduction = refrigerantLeakReduction + lowGwpRefrigerants;
  const refrigerantsUnitsOfCarbonAfterRefrigerantReduction =
    baselineCarbonWithGrowthAndCleanerGrid.refrigerants *
    (1 - refrigerantReduction);
  const carbonImpactRefrigerant =
    refrigerantsUnitsOfCarbonAfterRefrigerantReduction -
    baselineCarbonWithGrowthAndCleanerGrid.refrigerants;
  const refrigerantsCostScore = -carbonImpactRefrigerant * CostScore.Two; // weighted avg in workbook - should calculate based on specific selected solutions, as with buy clean options

  const carbonCapture = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.CarbonCaptureAndSequestrationCarbon,
  );
  const fugitiveEmissions = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.EliminateFugitiveEmissionsCarbon,
  );
  const offsets = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.OffsetsCarbon,
  );

  const carbonSolutionsTotal = carbonCapture + fugitiveEmissions + offsets;

  const totalCarbonBeforeSolutions = Object.values(
    baselineCarbonWithGrowthAndCleanerGrid,
  ).reduce((acc, val) => acc + val, 0);

  const totalCarbonAfterCleanPowerPurchase =
    totalCarbonBeforeSolutions +
    (efficiencyComponent?.carbonImpact || 0) +
    (stationaryElectrificationComponent?.carbonImpact || 0) +
    (mobileElectrificationComponent?.carbonImpact || 0) +
    (buyCleanComponent?.carbonImpact || 0);

  const carbonImpactCarbonCapture =
    totalCarbonAfterCleanPowerPurchase * (1 - carbonCapture) -
    totalCarbonAfterCleanPowerPurchase;
  const carbonImpactFugitiveEmissions =
    totalCarbonAfterCleanPowerPurchase * (1 - fugitiveEmissions) -
    totalCarbonAfterCleanPowerPurchase;
  const carbonImpactOffsets =
    totalCarbonAfterCleanPowerPurchase * (1 - offsets) -
    totalCarbonAfterCleanPowerPurchase;

  const carbonReductionCostScore =
    -carbonImpactCarbonCapture * CostScore.Five +
    -carbonImpactFugitiveEmissions * CostScore.Four +
    -carbonImpactOffsets * CostScore.Three;

  const otherCarbonImpact =
    carbonImpactRefrigerant +
    carbonImpactCarbonCapture +
    carbonImpactFugitiveEmissions +
    carbonImpactOffsets;

  const otherComponent: NullablePlanComponent =
    refrigerantLeakReduction > 0 ||
    lowGwpRefrigerants > 0 ||
    carbonCapture > 0 ||
    fugitiveEmissions > 0 ||
    offsets > 0
      ? {
          planComponentCategory: PlanComponentCategory.Other,
          intermediateCostScore:
            refrigerantsCostScore + carbonReductionCostScore,
          carbonImpact: otherCarbonImpact,
          fuelImpacts: [
            ...(refrigerantReduction > 0
              ? [
                  {
                    fuelSource: FuelSource.Refrigerants,
                    carbonImpact: carbonImpactRefrigerant,
                  },
                ]
              : []),
            ...(carbonSolutionsTotal > 0
              ? combineFuelImpacts([
                  {
                    fuelSource: FuelSource.Carbon,
                    carbonImpact: carbonImpactCarbonCapture,
                  },
                  {
                    fuelSource: FuelSource.Carbon,
                    carbonImpact: carbonImpactFugitiveEmissions,
                  },
                  {
                    fuelSource: FuelSource.Carbon,
                    carbonImpact: carbonImpactOffsets,
                  },
                ])
              : []),
          ],
        }
      : null;

  return otherComponent;
};
